import { altKeyOnly, platformModifierKeyOnly } from 'ol/events/condition';
import MapBrowserEventType from 'ol/MapBrowserEventType';
import Translate from 'ol/interaction/Translate';

import { InteractionGroup } from 'cadenza/map/interactions/interaction-group';
import { SnapInteraction } from 'cadenza/map/interactions/snap-interaction';
import { SelectOverlayInteraction } from 'cadenza/map/interactions/select-overlay-interaction';
import { createModifyPointStyle, getSelectStyle } from 'cadenza/map/style/select-styles';
import { HIT_TOLERANCE } from 'cadenza/map/map-constants';
import { SketchFeatureProperty as FeatureProperty } from 'cadenza/map/classic-sketch-layer/sketch-feature-property';
import {
  createBoxSelectionInteraction,
  createImmediateSelectInteraction } from 'cadenza/map/interactions/common-modify-interaction-group';
import { TranslateByHandleInteraction } from 'cadenza/map/interactions/translate-by-handle-interaction';
import { ClassicModifyInteraction } from 'cadenza/map/interactions/classic/classic-modify-interaction';

/**
 * @deprecated Will be removed when Classic and Workbook will be separated.
 * If you're looking for a similar class (without the group feature support) checkout
 * {@link import('cadenza/map/interactions/common-modify-interaction-group.js').CommonModifyInteractionGroup}
 */
export class ClassicSketchModifyInteractionGroup extends InteractionGroup {

  _select;

  constructor (layer, options = {}) {
    const source = layer.getSource();
    const featureFilter = feature => !feature.get(FeatureProperty.IS_UNSELECTABLE);
    const select = new SelectOverlayInteraction({
      condition: event => (event.type === MapBrowserEventType.CLICK),
      toggleCondition: platformModifierKeyOnly,
      hitTolerance: HIT_TOLERANCE,
      filter: featureFilter,
      getSelectStyle,
      layers: [ layer ]
    }, featuresAndLayers => featuresAndLayers
      .flatMap(([ hitFeature, hitLayer ]) => withOtherGroupFeatures(layer, [ hitFeature ])
        .map(groupFeature => [ groupFeature, hitLayer ])));
    const dragBoxSelect = createBoxSelectionInteraction(layer, select.getFeatures(), hitFeatures => withOtherGroupFeatures(layer, hitFeatures));
    const modify = createModifyInteraction(layer, select.getFeatures(), HIT_TOLERANCE, options.map);
    const immediateSelect = createImmediateSelectInteraction(layer, select.getFeatures(), HIT_TOLERANCE,
      hitFeature => withOtherGroupFeatures(layer, [ hitFeature ]));
    const translate = new Translate({
      features: select.getFeatures(),
      hitTolerance: HIT_TOLERANCE,
      filter: featureFilter
    });
    const snap = new SnapInteraction({ source });
    const interactions = [ select, dragBoxSelect, translate, modify, immediateSelect, snap ];
    if (options.withTranslateByHandle) {
      const translateByHandle = new TranslateByHandleInteraction({
        features: select.getFeatures(),
        hitTolerance: HIT_TOLERANCE,
        filter: featureFilter
      });
      interactions.push(translateByHandle);
    }
    super(...interactions);
    this._select = select;
  }

  getSelectedFeatures () {
    return this._select.getFeatures();
  }

}

// Returns new array containing all passed features and additionally all features in layer, which
// belong to the same groups as the passed features.
function withOtherGroupFeatures (layer, features) {
  if (!layer) {
    return features;
  }
  const groups = new Set();
  const featureIds = new Set();
  for (const feature of features) {
    const group = feature.get(FeatureProperty.GROUP);
    if (group) {
      groups.add(group);
    }
    featureIds.add(feature.getId());
  }
  const allFeatures = layer.getSource().getFeatures();
  const additionalFeatures = [];
  for (const feature of allFeatures) {
    const group = feature.get(FeatureProperty.GROUP);
    if (group && groups.has(group) && !featureIds.has(feature.getId())) {
      additionalFeatures.push(feature);
      featureIds.add(feature.getId());
    }
  }
  return features.concat(additionalFeatures);
}

function createModifyInteraction (layer, features, hitTolerance, map) {
  return new ClassicModifyInteraction({
    features,
    sketchLayer: layer,
    // Insert vertex only with ALT modifier pressed, so we are able to drag the whole line/polyline. Otherwise the modify interaction wins and adds new vertices instead of allowing to drag the geometry.
    insertVertexCondition: event => !event.immediateSelectionHandled && altKeyOnly(event),
    pixelTolerance: hitTolerance,
    style: createModifyPointStyle(),
    map
  });
}
